import { FinanceRow } from "@/types";
import dayjs from "dayjs";

export type FinanceTableHeader = {
  label: string;
  key: keyof FinanceRow | string;
  getValue?: (row: FinanceRow) => React.ReactNode;
  sortable?: boolean;
};

// Helper function to format currency amounts
const formatCurrency = (amount: string | number): string => {
  const numericAmount = parseFloat(String(amount) || "0");
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(numericAmount);
};

export const financeTableHeaders: FinanceTableHeader[] = [
  {
    label: "ID",
    key: "id",
    getValue: (row: FinanceRow) => row.id,
    sortable: true,
  },
  { label: "Applicant Name", key: "customerName", sortable: true },
  { label: "Applicant Address", key: "address", sortable: true },
  { label: "Applicant Email", key: "accountEmail", sortable: true },
  { label: "Applicant City", key: "city", sortable: true },
  { label: "Applicant State", key: "applicantState", sortable: true },
  { label: "Applicant Zip", key: "zipCode", sortable: true },
  { label: "Contractor", key: "contractor", sortable: true },
  { label: "Payee", key: "payee", sortable: true },
  { label: "Payee Type", key: "payeeType", sortable: true },
  { label: "LDC Account Number", key: "ldcAccountNumber", sortable: true },
  { label: "NEM/SBP", key: "nemSbp", sortable: true },
  {
    label: "NEM Start Date",
    key: "nemStartDate",
    sortable: true,
    getValue: (row: FinanceRow) =>
      row.nemStartDate ? dayjs(row.nemStartDate).format("MMMM D, YYYY") : "",
  },
  {
    label: "SBP Start Date",
    key: "sbpStartDate",
    sortable: true,
    getValue: (row: FinanceRow) =>
      row.sbpStartDate ? dayjs(row.sbpStartDate).format("MMMM D, YYYY") : "",
  },
  { label: "Utility Rate", key: "utilityRate", sortable: true },
  {
    label: "Utility Rate Start Date",
    key: "utilityRateStartDate",
    sortable: true,
    getValue: (row: FinanceRow) =>
      row.utilityRateStartDate
        ? dayjs(row.utilityRateStartDate).format("MMMM D, YYYY")
        : "",
  },
  {
    label: "New Solar System Size Installed",
    key: "newSolarSystemSize",
    getValue: (row: FinanceRow) => row.newSolarSystemSize || "",
    sortable: true,
  },
  { label: "Battery Manufacturer", key: "batteryOem", sortable: true },
  { label: "Battery Product", key: "batteryModel", sortable: true },
  { label: "Battery S/N", key: "batterySerialNumber", sortable: true },
  { label: "# of Batteries", key: "numberOfBatteries", sortable: true },
  { label: "Battery Nameplate kW", key: "batteryNameplateKw", sortable: true },
  {
    label: "Battery Usable Capacity",
    key: "usableCapacity",
    getValue: (row: FinanceRow) =>
      row.usableCapacity ? `${row.usableCapacity} kWh` : "",
    sortable: true,
  },
  { label: "PTO Nameplate kW", key: "ptoNameplateKw", sortable: true },
  { label: "PTO Solar kW-AC", key: "ptoSolarKwAc", sortable: true },
  { label: "CoC/Care/Fera", key: "cocCareFera", sortable: true },
  { label: "Type", key: "projectType", sortable: true },
  {
    label: "Upfront Incentive Amount",
    key: "upfrontIncentivePayment",
    getValue: (row: FinanceRow) => formatCurrency(row.upfrontIncentivePayment),
    sortable: true,
  },
  {
    label: "Eligible Project Cost",
    key: "total_cost",
    getValue: (row: FinanceRow) => formatCurrency(row.total_cost),
    sortable: true,
  },
  { label: "T&Cs", key: "termsAndConditions", sortable: true },
  {
    label: "PTO Letter Date",
    key: "ptoLetterDate",
    sortable: true,
    getValue: (row: FinanceRow) =>
      row.ptoLetterDate ? dayjs(row.ptoLetterDate).format("MMMM D, YYYY") : "",
  },
  {
    label: "PTO Submitted Date",
    key: "ptoSubmittedDate",
    sortable: true,
    getValue: (row: FinanceRow) =>
      row.ptoSubmittedDate
        ? dayjs(row.ptoSubmittedDate).format("MMMM D, YYYY")
        : "",
  },
  {
    label: "Date Moved to Finance Review",
    key: "finance_review_date",
    sortable: true,
    getValue: (row: FinanceRow) =>
      row.finance_review_date
        ? dayjs(row.finance_review_date).format("MMMM D, YYYY")
        : "",
  },
  { label: "Notes", key: "note", sortable: false },
];
